import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@radix-ui/react-collapsible"
import clsx from "clsx"
import {
  createContext,
  forwardRef,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from "react"

import { Card, CardContent } from "../Card"
import { Icon } from "../Icon"
import { Text } from "../Text"
import styles from "./FileExplorer.module.css"
import {
  FileExplorerProvider,
  type FileExplorerSelectedFile,
} from "./FileExplorerProvider"
import { useFileExplorerContext } from "./useFileExplorerContext"

type FileExplorerProps = {
  className?: string
  children: ReactNode
  onSelectedChange?: (selected: FileExplorerSelectedFile | null) => void
} & (
  | { selected?: FileExplorerSelectedFile | null; defaultSelected?: never }
  | { defaultSelected?: FileExplorerSelectedFile | null; selected?: never }
)

export function FileExplorer(props: FileExplorerProps) {
  const { selected, defaultSelected, className, children, onSelectedChange } =
    props

  const contents = (
    <div className={className}>
      <Card
        color="grey2"
        paddingBlock={0}
        paddingInline={16}
        sx={{ height: "100%" }}
      >
        <CardContent sx={{ height: "100%" }}>
          <div className={styles.contentRoot}>{children}</div>
        </CardContent>
      </Card>
    </div>
  )

  if (selected !== undefined)
    return (
      <FileExplorerProvider
        selected={selected}
        onSelectedChange={onSelectedChange}
      >
        {contents}
      </FileExplorerProvider>
    )

  return (
    <FileExplorerProvider
      defaultSelected={defaultSelected}
      onSelectedChange={onSelectedChange}
    >
      {contents}
    </FileExplorerProvider>
  )
}

type FileExplorerListProps = {
  header?: ReactNode
  children: ReactNode
}

export function FileExplorerList(props: FileExplorerListProps) {
  const { header, children } = props

  return (
    <div className={styles.list}>
      <div className={styles.listHeader}>{header}</div>
      <FileExplorerListFiles className={styles.listFilesRoot}>
        {children}
      </FileExplorerListFiles>
    </div>
  )
}

type FileExplorerFolderContextContent = {
  path: string[]
}

const FileExplorerFolderContext =
  createContext<FileExplorerFolderContextContent>({ path: [] })

function useFileExplorerFolder() {
  return useContext(FileExplorerFolderContext)
}

type FileExplorerFolderProps = {
  name: string
  defaultOpen?: true
  children?: ReactNode
}

export function FileExplorerFolder(props: FileExplorerFolderProps) {
  const { name, defaultOpen = false, children } = props

  const [open, setOpen] = useState(defaultOpen)
  const { selected } = useFileExplorerContext()
  const parent = useFileExplorerFolder()
  const contextValue = useMemo(
    () => ({ path: [...parent.path, name] }),
    [parent.path, name],
  )
  const childIsSelected = selected
    ? joinPath(selected.path).startsWith(joinPath(contextValue.path))
    : false

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <FileExplorerFolderContext.Provider value={contextValue}>
        <li
          className={clsx(
            styles.listFolder,
            !open && childIsSelected && styles["listFolder-selected"],
          )}
        >
          <CollapsibleTrigger asChild>
            <button
              className={styles.listFolderTrigger}
              style={{ "--FileExplorer-folder-depth": parent.path.length }}
            >
              <Icon
                name="chevronRight"
                size="small"
                color="grey12"
                className={styles.listFolderTriggerIcon}
              />
              <Text component="span" color="grey11">
                {name}
              </Text>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent forceMount asChild>
            <FileExplorerListFiles>{children}</FileExplorerListFiles>
          </CollapsibleContent>
        </li>
      </FileExplorerFolderContext.Provider>
    </Collapsible>
  )
}

type FileExplorerFileProps = {
  name: string
  contents: ReactNode | { __html: string }
  language?: string
  endAdornment?: ReactNode
  onSelect?: () => void
}

export function FileExplorerFile(props: FileExplorerFileProps) {
  const {
    name,
    contents,
    language,
    endAdornment,
    onSelect: providedOnSelect,
  } = props

  const fileExplorer = useFileExplorerContext()
  const folder = useFileExplorerFolder()
  const path = [...folder.path, name]
  const isSelected = fileExplorer.selected
    ? joinPath(fileExplorer.selected.path) === joinPath(path)
    : false

  function onSelect() {
    fileExplorer.setSelected({ path, contents, language })
    providedOnSelect?.()
  }

  return (
    <li
      className={clsx(
        styles.listFile,
        isSelected && styles["listFile-selected"],
      )}
    >
      <button
        onClick={onSelect}
        className={styles.listFileTrigger}
        style={{ "--FileExplorer-folder-depth": folder.path.length }}
      >
        <Icon name="insertDriveFile" size="small" color="grey11" />
        <Text component="span">{name}</Text>
        {endAdornment ? (
          <div className={styles.listFileTriggerEndAdornment}>
            {endAdornment}
          </div>
        ) : null}
      </button>
    </li>
  )
}

type FileExplorerLabelProps = {
  label: ReactNode
}

export function FileExplorerLabel(props: FileExplorerLabelProps) {
  const { label } = props

  const folder = useFileExplorerFolder()

  return (
    <li
      className={styles.listLabel}
      style={{ "--FileExplorer-folder-depth": folder.path.length }}
    >
      <Text component="span" color="grey8">
        {label}
      </Text>
    </li>
  )
}

type FileExplorerListFilesProps = {
  className?: string
  children: ReactNode
}

const FileExplorerListFiles = forwardRef<
  HTMLUListElement,
  FileExplorerListFilesProps
>(function FileExplorerListFiles(props, ref) {
  const { children, className, ...otherProps } = props

  return (
    <ul ref={ref} className={clsx(styles.listFiles, className)} {...otherProps}>
      {children}
    </ul>
  )
})

type FileExplorerViewerProps = {
  children?: ReactNode
}

export function FileExplorerViewer(props: FileExplorerViewerProps) {
  const { children } = props

  const { selected } = useFileExplorerContext()

  if (!selected)
    return (
      <div className={styles.viewer}>
        <div className={styles.viewerPath} />
        {children}
      </div>
    )

  return (
    <div className={styles.viewer}>
      <div className={styles.viewerPath}>
        <Icon name="insertDriveFile" size="small" color="grey11" />
        <Text>{joinPath(selected.path)}</Text>
      </div>
      {typeof selected.contents === "object" &&
      selected.contents !== null &&
      "__html" in selected.contents ? (
        <div
          key={joinPath(selected.path)}
          className={styles.viewerContents}
          dangerouslySetInnerHTML={selected.contents}
        />
      ) : (
        <div className={styles.viewerContents}>{selected.contents}</div>
      )}
    </div>
  )
}

function joinPath(path: string[]) {
  return path.join("/")
}
